import { TextField } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

export default function EmailChips(props) {
    const [emailValue, setEmailValue] = useState(null);
    const [error, setError] = useState(null);
    // Polite live-region text announced by screen readers on add/remove (AC-4).
    const [announcement, setAnnouncement] = useState("");

    const inputRef = useRef(null);
    const errorRef = useRef(null);
    // Per-chip remove-button refs + a pending-focus target applied after the list re-renders, so focus
    // lands on the next remaining chip's remove button (or the input when the list empties) (AC-5).
    const removeButtonRefs = useRef([]);
    const pendingFocusRef = useRef(null);

    const emailAlreadyInList = email => props.emailsList.includes(email);

    const focusInput = () => {
        if (inputRef.current) inputRef.current.focus();
    };

    // When an error appears, scroll it into view so the message is visible (AC-2). NVDA announces it
    // via role="alert"; scrolling guarantees a sighted user also sees it.
    useEffect(() => {
        if (error && errorRef.current && typeof errorRef.current.scrollIntoView === "function") {
            errorRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
    }, [error]);

    // After a removal re-renders the list, move focus to the resolved target (next remaining chip's
    // remove button, or the input when the list is now empty) (AC-5).
    useEffect(() => {
        if (pendingFocusRef.current == null) return;
        const target = pendingFocusRef.current;
        pendingFocusRef.current = null;
        if (target === "input") {
            focusInput();
        } else {
            const btn = removeButtonRefs.current[target];
            if (btn) btn.focus();
            else focusInput();
        }
    }, [props.emailsList]);

    const isValid = value => {
        let e = null;

        if (emailAlreadyInList(value)) e = `${value} has already been added`;

        const EMAIL_REGEX = /^\w+([\.-]?[\+]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/

        if (!EMAIL_REGEX.test(value)) e = `${value} is not a valid email`;

        setError(e);

        if (e) return false;

        return true;
    }

    const handleKeyDown = event => {
        if (["Enter", "Tab", ","].includes(event.key)) {
            var value = emailValue ? emailValue.trim() : '';

            if (!value) {
                if (event.key === 'Tab' && props.onTabOut) {
                    event.preventDefault();
                    props.onTabOut();
                }
                return;
            }

            event.preventDefault();

            if (isValid(value)) {
                if (props.onEmailEmpty) props.onEmailEmpty();
                setEmailValue(null);
                props.addEmails(value);
                setAnnouncement(`Target email ${value} added`);
            }
        }
    }

    const handleChange = event => {
        if (event.target.value.length > 0 && props.onEmailInput) props.onEmailInput();
        else if (props.onEmailEmpty && event.target.value.length === 0) props.onEmailEmpty();

        if (error) setError(null);

        setEmailValue(event.target.value)
    };

    const handlePaste = event => {
        event.preventDefault();

        var paste = event.clipboardData.getData("text");
        var emails = paste.match(/[\w\d\.-]+@[\w\d\.-]+\.[\w\d\.-]+/g);

        if (emails) {
            var toBeAdded = emails.filter(email => !emailAlreadyInList(email));

            if (toBeAdded.length > 0) {
                props.addEmails(...toBeAdded);
                setAnnouncement(
                    toBeAdded.length === 1
                        ? `Target email ${toBeAdded[0]} added`
                        : `${toBeAdded.length} target emails added`
                );
            }
        }
    }

    const handleRemove = (index, value) => {
        const remaining = props.emailsList.length - 1;
        // Move focus to the next remaining chip's remove button. After removal the list shifts left,
        // so the chip that was at index+1 becomes index. If we removed the last chip, focus the new
        // last one (index-1). When no chips remain, fall back to the input (AC-5).
        if (remaining <= 0) {
            pendingFocusRef.current = "input";
        } else if (index < remaining) {
            pendingFocusRef.current = index;
        } else {
            pendingFocusRef.current = remaining - 1;
        }
        props.removeItem(index);
        setAnnouncement(`Target email ${value} removed`);
    };

    return (<div className="emailChipsContainer">
        <section className="emailChipsDisplayList">
            {props.emailsList.map((v, i) => (<div aria-label="emails" className="emailChip" key={`emailChip${i}`}>
                {v}
                {/* Unique, descriptive name so each button announces its own email (AC-1). */}
                <button aria-label={`Remove target email ${v}`} className="emailChipRemoveButton"
                        ref={el => { removeButtonRefs.current[i] = el; }}
                        onClick={() => handleRemove(i, v)}>
                    &times;
                </button>
            </div>))}
            <TextField className={`emailChipsInput ${error && 'hasError'}`} value={emailValue || ''}
                       placeholder={props.inputPlaceholder || "Type or paste email addresses and press `Enter`..."}
                       onKeyDown={handleKeyDown}
                       onChange={handleChange}
                       onPaste={handlePaste}
                       fullWidth
                       autoComplete="off"
                       inputRef={inputRef}
                       inputProps={{
                           "aria-label": props.inputAriaLabel || "Email input",
                           ...(props.inputAriaRequired && { "aria-required": props.inputAriaRequired }),
                           // Mark invalid for assistive tech, but do NOT add aria-describedby pointing at
                           // the alert — role="alert" already announces it once; describedby would make
                           // NVDA read the same message a second time when focus is in the field (AC-2).
                           ...(error && { "aria-invalid": true }),
                           "data-1p-ignore": true,
                           "data-lpignore": true
                       }}
            />
            {/* Always-mounted assertive live region: announces ONLY the message text on change (no
                "alert" role word), exactly once. Visible + occupies space only when there's an error (AC-2). */}
            <p ref={errorRef} className={error ? "error" : "visuallyHidden"} aria-live="assertive">{error || ''}</p>
        </section>
        {/* Visually-hidden polite live region for add/remove announcements (AC-4). */}
        <div aria-live="polite" role="status" className="visuallyHidden">{announcement}</div>
    </div>);
}
